import { AttendanceStatus } from "../features/jacana/types";
import { callBackendFunction } from "./paymentService";

export interface BookingMutationResponse {
  success: boolean;
  noop?: boolean;
  inProgress?: boolean;
  eventId?: string;
  bookingId?: string;
  outingId?: string;
  bookingStatus?: string;
  paymentStatus?: string;
  refundStatus?: string;
  attendanceStatus?: string;
  partialRefundStatus?: string;
  partialCancellationCount?: number;
  activeParticipantsCount?: number;
  cancelledParticipantsCount?: number;
  becameFullyCancelled?: boolean;
  idempotentReplay?: boolean;
  replayCancellationByRate?: Record<string, number>;
  refundAmountRequestedDelta?: number;
  refundAmountRequested?: number;
  ibanMasked?: string;
  refundRequired?: boolean;
  processedCount?: number;
  releasedParticipants?: number;
}

export interface CancelBookingParams {
  bookingId: string;
}

export interface CancelBookingParticipantsParams {
  bookingId: string;
  cancellationByRate: Record<string, number>;
  idempotencyKey: string;
  reason?: string;
}

export interface ReportAbsenceParams {
  bookingId: string;
}

export interface ValidateManualPaymentParams {
  bookingId: string;
}

export interface MarkAttendanceParams {
  bookingId: string;
  attendanceStatus: Extract<AttendanceStatus, "attended" | "no_show">;
}

export interface ProcessRefundParams {
  bookingId: string;
  action?: "refund" | "refuse";
  refundMethod?: "manual" | "provider";
  refundReason?: string;
}

export interface CancelOutingAndCascadeParams {
  outingId: string;
  reason?: string;
  sendEmails?: boolean;
  organizerMessage?: string;
}

export interface SubmitPartialRefundIbanParams {
  bookingId: string;
  iban: string;
  accountHolderName: string;
}

export interface GetPartialRefundPaymentPayloadParams {
  bookingId: string;
}

export interface PartialRefundPaymentPayloadResponse {
  success: boolean;
  bookingId: string;
  beneficiary: string;
  iban: string;
  amount: number;
  communication: string;
  qrPayload?: string | null;
  qrCodeDataUrl?: string | null;
}

export interface ProcessPartialRefundParams {
  bookingId: string;
  action: 'mark_refunded' | 'refuse';
  reason?: string;
}

function randomHex(bytes = 8) {
  const safeBytes = Math.max(1, Math.trunc(bytes));

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const values = new Uint8Array(safeBytes);
    crypto.getRandomValues(values);
    return Array.from(values, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return Math.random().toString(16).slice(2, 2 + safeBytes * 2).padEnd(safeBytes * 2, "0");
}

export function generateClientIdempotencyKey(prefix = "pc") {
  const normalizedPrefix = String(prefix || "pc").trim() || "pc";
  const timestampPart = Date.now().toString(36);
  const randomPart = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : randomHex(16);

  return `${normalizedPrefix}_${timestampPart}_${randomPart}`;
}

export const bookingActionsService = {
  async cancelBooking(params: CancelBookingParams) {
    return callBackendFunction<CancelBookingParams, BookingMutationResponse>("cancelBooking", params);
  },

  async cancelBookingParticipants(params: CancelBookingParticipantsParams) {
    return callBackendFunction<CancelBookingParticipantsParams, BookingMutationResponse>("cancelBookingParticipants", params);
  },

  async reportAbsence(params: ReportAbsenceParams) {
    return callBackendFunction<ReportAbsenceParams, BookingMutationResponse>("reportAbsence", params);
  },

  async validateManualPayment(params: ValidateManualPaymentParams) {
    return callBackendFunction<ValidateManualPaymentParams, BookingMutationResponse>("validateManualPayment", params);
  },

  async markAttendance(params: MarkAttendanceParams) {
    return callBackendFunction<MarkAttendanceParams, BookingMutationResponse>("markAttendance", params);
  },

  async processRefund(params: ProcessRefundParams) {
    return callBackendFunction<ProcessRefundParams, BookingMutationResponse>("processRefund", params);
  },

  async cancelOutingAndCascade(params: CancelOutingAndCascadeParams) {
    return callBackendFunction<CancelOutingAndCascadeParams, BookingMutationResponse>("cancelOutingAndCascade", params);
  },

  async submitPartialRefundIban(params: SubmitPartialRefundIbanParams) {
    return callBackendFunction<SubmitPartialRefundIbanParams, BookingMutationResponse>("submitPartialRefundIban", params);
  },

  async getPartialRefundPaymentPayload(params: GetPartialRefundPaymentPayloadParams) {
    return callBackendFunction<GetPartialRefundPaymentPayloadParams, PartialRefundPaymentPayloadResponse>("getPartialRefundPaymentPayload", params);
  },

  async processPartialRefund(params: ProcessPartialRefundParams) {
    return callBackendFunction<ProcessPartialRefundParams, BookingMutationResponse>("processPartialRefund", params);
  },
};
